import { AnyAction } from 'redux';
import _clamp from 'lodash/clamp';
import * as itemTypes from '../../constants/itemTypes/itemTypes';
import { UP, DOWN, BEFORE } from '../../constants/misc';
import { IBlock, IColumn, IGeneratedIDs } from '../../types/content';
import { moveArrayElements } from '../common/moveArrayElements';

export default {
    populateInitialBlocks: (state: IBlock[], action: AnyAction) => {
        const { initialBlocksState }: { initialBlocksState: IBlock[] } = action.payload;
        const indices = initialBlocksState.map((block) => block.index);
        const duplicatesRemoved = initialBlocksState.filter((block, i) => indices.indexOf(block.index) === i);
        const columnsSanitized = duplicatesRemoved.map((block) => ({
            ...block,
            content: {
                ...block.content,
                columns: block.content.columns.filter((column) => !!column),
            },
        }));
        state.length = 0;
        state.push(...columnsSanitized);
    },

    switchLockStructure: (state: IBlock[], action: AnyAction) => {
        const { blockIdx, prev } = action.payload;
        state.forEach((block) => {
            if (block.index === blockIdx) {
                block.content.structureLocked = !prev;
            }
        });
    },

    addBlock: (state: IBlock[], action: AnyAction) => {
        const { droppedBlockIdx, block, indices } = action.payload;
        let idx1 = 0;
        let idx2 = 0;
        const generatedColumns = block.content.columns.map((column: IColumn) => {
            const { columnItems } = column;
            const newColumnIndex = indices.column[idx1];
            idx1 += 1;
            const updatedColumnItems = columnItems.map((columnItem) => {
                const newElementIndex = indices.element[idx2];
                idx2 += 1;
                return { ...columnItem, elmIdx: newElementIndex };
            });
            return { ...column, columnIdx: newColumnIndex, columnItems: updatedColumnItems };
        });
        const newBlock = {
            ...block,
            index: indices.block[0],
            type: itemTypes.CUSTOMIZED_BLOCK,
            content: {
                ...block.content,
                columns: generatedColumns,
            },
        };
        let insertPosIndex;
        if (droppedBlockIdx !== undefined) {
            insertPosIndex = droppedBlockIdx;
        } else {
            insertPosIndex = state.length;
        }
        state.splice(insertPosIndex, 0, newBlock);
    },

    duplicateBlocks: (state: IBlock[], action: AnyAction) => {
        const { blocks, indices }: { blocks: IBlock[]; indices: IGeneratedIDs } = action.payload;
        let idx1 = 0;
        let idx2 = 0;
        const insertionIndex = state.findIndex((elm) => elm.index === blocks[blocks.length - 1].index) + 1;
        const duplicatedBlocks = blocks.map((block, index) => {
            const updatedColumns = block.content.columns.map((column: IColumn) => {
                const { columnItems } = column;
                const newColumnIndex = indices.column[idx1];
                idx1 += 1;
                const updatedColumnItems = columnItems.map((columnItem) => {
                    const newElementIndex = indices.element[idx2];
                    idx2 += 1;
                    return { ...columnItem, elmIdx: newElementIndex };
                });
                return {
                    ...column,
                    columnIdx: newColumnIndex,
                    columnItems: updatedColumnItems,
                };
            });
            const newBlockID = indices.block[index];
            return {
                ...block,
                index: newBlockID,
                type: itemTypes.CUSTOMIZED_BLOCK,
                content: {
                    ...block.content,
                    columns: updatedColumns,
                },
            };
        });
        state.splice(insertionIndex, 0, ...duplicatedBlocks);
    },

    removeBlock: (state: IBlock[], action: AnyAction) => {
        const block = action.payload;
        state.splice(
            state.findIndex((elm) => elm.index === block.index),
            1
        );
    },

    moveBlocks: (state: IBlock[], action: AnyAction) => {
        const {
            blocks,
            direction,
            isToEdge = false,
        }: { blocks: IBlock[]; direction: typeof UP | typeof DOWN; isToEdge: boolean } = action.payload;
        const isDirectionUp = direction === UP;
        const selectedBlockIDs = blocks.map((block) => block.index);
        const filteredState = state.filter((block) => !selectedBlockIDs.includes(block.index));
        const anchorBlockIndex = state.findIndex(
            (block) => block.index === blocks[isDirectionUp ? 0 : blocks.length - 1].index
        );
        if (
            blocks.length === 1 &&
            ((anchorBlockIndex === 0 && isDirectionUp) || (anchorBlockIndex === state.length - 1 && !isDirectionUp))
        ) {
            // User is attempting to move top block up or bottom block down; don't do anything
            return state;
        }
        if (isToEdge) {
            return isDirectionUp ? [...blocks, ...filteredState] : [...filteredState, ...blocks];
        }
        if (blocks.length > 1 && (anchorBlockIndex === 0 || anchorBlockIndex === state.length - 1)) {
            // Multiple blocks are selected that include the first or last block, so just insert there
            filteredState.splice(anchorBlockIndex, 0, ...blocks);
            return filteredState;
        }
        // using splice() without deleting anything bumps whatever what was at the current index up,
        // so when moving up, subtract 1, but when moving down we need to add 2 to move below the next block
        const nextBlock = state[_clamp(isDirectionUp ? anchorBlockIndex - 1 : anchorBlockIndex + 2, 0, state.length)];
        const moveToIndex = nextBlock
            ? filteredState.findIndex((block) => block.index === nextBlock.index)
            : filteredState.length;
        filteredState.splice(moveToIndex, 0, ...blocks);
        return filteredState;
    },

    swapBlocks: (state: IBlock[], action: AnyAction) => {
        const { draggedBlockIdx, droppedBlockIdx = state.length, position } = action.payload;
        const newBlockIndex = position === BEFORE ? droppedBlockIdx : droppedBlockIdx + 1;
        return moveArrayElements(state, draggedBlockIdx, newBlockIndex);
    },

    lockBlocks: (state: IBlock[], action: AnyAction) => {
        const { blockIDs } = action.payload;
        const allLocked = state.filter((block) => block.content.structureLocked).length === blockIDs.length;
        if (blockIDs.length === 1) {
            state.forEach((block) => {
                if (block.index === blockIDs[0]) {
                    block.content.structureLocked = !block.content.structureLocked;
                }
            });
        } else {
            state.forEach((block) => {
                if (blockIDs.includes(block.index)) {
                    block.content.structureLocked = allLocked ? !block.content.structureLocked : true;
                }
            });
        }
    },

    deleteBlocks: (state: IBlock[], action: AnyAction) => {
        const { blockIDs } = action.payload;
        const validBlockIDs: string[] = [];
        state.forEach((block) => {
            const id = block.index;
            const blockIsLocked = block.content.structureLocked;
            if (blockIDs.includes(id) && !blockIsLocked) {
                validBlockIDs.push(id);
            }
        });
        return state.filter((block) => !validBlockIDs.includes(block.index));
    },

    updateBlocksProperties: (state: IBlock[], action: AnyAction) => {
        const { blockIDs, property, isMetaData = false } = action.payload;
        const { keys, value } = property;
        state.forEach((block) => {
            const blockIsLocked = block.content.structureLocked;
            if (blockIDs.includes(block.index) && !blockIsLocked) {
                keys.forEach((key: string) => {
                    if (isMetaData) {
                        block[key as keyof IBlock] = value;
                    } else {
                        // @ts-ignore can't assign to 'never' issue
                        block.content[key] = value;
                    }
                });
            }
        });
    },
};
